import React, { useMemo, useState, useCallback } from "react"
import { Switch, Form, Input, InputNumber, Flex, Button, Tooltip, Select, Checkbox, Mentions } from "antd"
import TextArea from "antd/es/input/TextArea"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { v4 as uuidv4 } from "uuid"
import { useSelector } from "react-redux"
import { flushSync } from "react-dom"
import { buildMasterColumnOptions } from "../../../utils/masterHelper"
import { buildMasterBackedWidgetTranslations, mergeTableColumnTranslations } from "../../../utils/masterTranslations.js"
import { getSingleMasterData } from "../../../services/adminServices"


const TableForm = ({ formValues, form, hasDefaultValues = false }) => {
    const { t } = useTranslation();
    const { masterList } = useSelector((state) => state.masterData);

    const inputTypeOptions = [
        { label: t('Input'), value: "input" },
        { label: t('Textarea'), value: "textarea" },
        { label: t('Checkbox'), value: "checkbox" },
        { label: t('Multple Choice'), value: "mcq" },
        { label: t('Radio'), value: "radio" },
        { label: t('Link'), value: "link" },
        { label: t('Date'), value: "date" },
        { label: t('Time'), value: "time" },
        { label: t('Number'), value: "number" },
        { label: t('Dropdown'), value: "dropdown" },
        { label: t('User'), value: "user" }
    ];

    const masterTableOptions = useMemo(() => {
        const currentMasterList = Array.isArray(masterList) ? masterList : [];
        return currentMasterList
            .filter((data) => (data.fields && data.fields.length > 0) && data.dataName !== "CONFIGURATION")
            .map((data) => ({ label: data.dataName, value: data.dataId }));
    }, [masterList]);

    const getMasterColumns = (tableId) => {
        if (!tableId) {
            return [];
        }

        const currentMasterList = Array.isArray(masterList) ? masterList : [];
        const tableData = currentMasterList.find((data) => data.dataId === tableId);
        return (tableData?.fields || []).map((field) => ({ label: field.label, value: field.label }));
    };

    const getMasterDataOptions = (tableId, columnLabel, tableOverride) => {
        if (!tableId || !columnLabel) {
            return [];
        }

        const currentMasterList = Array.isArray(masterList) ? masterList : [];
        const tableData = tableOverride || currentMasterList.find((data) => data.dataId === tableId);
        return buildMasterColumnOptions(tableData, columnLabel, currentMasterList);
    };

    const handleMasterTableChange = (index, tableId) => {
        const currentColumn = form.getFieldValue(["columns", index]) || {};
        form.setFieldValue(["columns", index], {
            ...currentColumn,
            parentTable: tableId,
            mappedColumn: undefined,
            isMasterData: Boolean(tableId),
            options: []
        });
    };

    const handleMasterColumnChange = async (index, columnLabel) => {
        const currentColumn = form.getFieldValue(["columns", index]) || {};
        const currentMasterList = Array.isArray(masterList) ? masterList : [];
        let tableData = currentMasterList.find((data) => data.dataId === currentColumn.parentTable);
        if (currentColumn.parentTable) {
            const response = await getSingleMasterData(currentColumn.parentTable)
            if (Array.isArray(response?.data?.fields)) {
                tableData = response.data
            }
        }
        const options = getMasterDataOptions(currentColumn.parentTable, columnLabel, tableData);
        const columnField = (tableData?.fields || []).find((field) => field?.label === columnLabel);

        form.setFieldValue(["columns", index], {
            ...currentColumn,
            mappedColumn: columnLabel,
            isMasterData: Boolean(currentColumn.parentTable && columnLabel),
            options
        });

        const columnTranslations = buildMasterBackedWidgetTranslations(
            columnField,
            tableData,
            columnLabel,
            options,
            Array.isArray(masterList) ? masterList : []
        )
        if (columnTranslations && currentColumn.id) {
            const bucket = { ...columnTranslations }
            if (currentColumn.label && currentColumn.label !== columnLabel) {
                Object.keys(bucket).forEach((lng) => {
                    if (bucket[lng]) {
                        const { label: _ignored, ...rest } = bucket[lng]
                        bucket[lng] = rest
                    }
                })
            }
            const merged = mergeTableColumnTranslations(
                form.getFieldValue("translations") || {},
                currentColumn.id,
                bucket
            )
            form.setFieldValue("translations", merged)
        }
    };

    const handleIsArchiveChange = (index, checked) => {
        const currentColumn = form.getFieldValue(["columns", index]) || {};
        if (checked) {
            const allColumns = form.getFieldValue("columns") || [];
            const masterColumn = allColumns.find((col) => col?.parentTable);
            currentColumn.parentTable = masterColumn?.parentTable || "";
        } else {
            delete currentColumn.parentTable;
        }
        form.setFieldValue(["columns", index], { ...currentColumn });
    };

    // --- Computed Number helpers for table columns ---
    const getOtherNumberColumns = useCallback((currentIndex) => {
        const allColumns = form.getFieldValue("columns") || []
        return allColumns
            .map((col, idx) => ({ ...col, _index: idx }))
            .filter((col, idx) => idx !== currentIndex)
            .map((col) => ({ label: col.label || `Column ${col._index + 1}`, value: col.label || `Column ${col._index + 1}`, id: col.id }))
    }, [form])

    const handleComputedFormulaChange = useCallback((index, value) => {
        const currentColumn = form.getFieldValue(["columns", index]) || {}
        if (!value) {
            form.setFieldValue(["columns", index], { ...currentColumn, formula: null, formulaHtml: null })
            return
        }

        const otherNumCols = getOtherNumberColumns(index)
        let htmlStr = value
        otherNumCols
            .sort((a, b) => b.label.length - a.label.length)
            .forEach((col) => {
                const pattern = '@' + col.label
                if (value.includes(pattern)) {
                    try {
                        const replacement = `<span class="widget-inline-block" data-label="${col.label}" data-value="${col.id}">${col.label}</span>`
                        htmlStr = htmlStr.replace(new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacement)
                    } catch { /* no-op */ }
                }
            })

        flushSync(() => {
            form.setFieldValue(["columns", index], { ...currentColumn, formula: value, formulaHtml: htmlStr })
        })
    }, [form, getOtherNumberColumns])

    const handleComputedToggle = useCallback((index, checked) => {
        const currentColumn = form.getFieldValue(["columns", index]) || {}
        if (!checked) {
            form.setFieldValue(["columns", index], { ...currentColumn, computedNumber: false, formula: null, formulaHtml: null })
        } else {
            form.setFieldValue(["columns", index], { ...currentColumn, computedNumber: true })
        }
    }, [form])

    const handleInputTypeChange = (index, inputType) => {
        const currentColumn = form.getFieldValue(["columns", index]) || {};
        const currentOptions = Array.isArray(currentColumn.options) ? currentColumn.options : [];
        const currentLabel = typeof currentColumn.label === "string" ? currentColumn.label.trim() : "";
        const shouldDefaultCheckboxLabel = !currentLabel || currentLabel.toLowerCase() === "name";

        let options = currentOptions;
        if (inputType === "checkbox" && currentOptions.length === 0) {
            options = [{ label: "isComplete", value: "isComplete" }];
        }

        form.setFieldValue(["columns", index], {
            ...currentColumn,
            inputType,
            label: inputType === "checkbox" && shouldDefaultCheckboxLabel ? "isComplete" : currentColumn.label,
            options,
        });
    };

    return (
        <>
            <Form.Item name="label" label={t('Label')}>
                <Input placeholder={t('Enter label')} />
            </Form.Item>

            <Form.Item name="tooltip" label={t('Tooltip')} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            {formValues && formValues.tooltip &&
                <Form.Item label="" name="helpText">
                    <TextArea rows={2} placeholder={t('Enter help text')} />
                </Form.Item>
            }

            <Form.Item name="mandatory" label={t('Mandatory')} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="readOnly" label={t('Read Only')} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="hideInitially" label={t('Hide Initially')} className="horizontal-form-item">
                <Switch size="small" />
            </Form.Item>

            {/* Only meaningful for a table prefilled with default rows — but stay
                visible once switched off, so it can always be switched back on. */}
            {(hasDefaultValues || formValues?.allowNewRow === false) &&
                <Form.Item name="allowNewRow" label={t('Allow New Row')} className="horizontal-form-item" initialValue={true}>
                    <Switch size="small" />
                </Form.Item>
            }

            {/* Table Rows - Updated to default to 1 row */}
            {/* <Form.Item name="rowCount" label="Number of Rows" initialValue={1}>
                <InputNumber 
                    min={1} 
                    max={50} 
                    placeholder="Enter number of rows"
                    style={{ width: "100%" }}
                />
            </Form.Item> */}

            <div style={{ marginBottom: "12px" }}>{t('Columns')}</div>
            <Form.List name="columns">
                {(fields, { add, remove }) => (
                    <>
                        <Flex className="guidelines-section">
                            <div style={{ width: "100%" }}>
                                {fields.map((field, index) => {
                                    const watchedInputType = form.getFieldValue(['columns', index, 'inputType']);
                                    const selectedMasterTable = form.getFieldValue(['columns', index, 'parentTable']);
                                    const selectedMasterColumn = form.getFieldValue(['columns', index, 'mappedColumn']);
                                    const isConnectedToMaster = Boolean(selectedMasterTable && selectedMasterColumn);
                                    const masterColumnOptions = getMasterColumns(selectedMasterTable);
                                    return (
                                        <div key={field.key} className="table-column-setup" style={{ marginBottom: "14px", padding: "16px", backgroundColor: "#fafafa", borderRadius: "10px", border: "1px solid #f0f0f0" }}>
                                            {/* Row 1: Column Name */}
                                            <Flex gap={10} align="center" justify="space-between">
                                                <Form.Item name={[index, "id"]} hidden>
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item name={[index, "label"]} style={{ marginBottom: 0, flex: 1 }} rules={[{ "required": true, message: t("Column is required") }]}> 
                                                    <Input placeholder={t("Enter column name")} />
                                                </Form.Item>
                                                <DeleteOutlined
                                                    style={{ color: "red", cursor: "pointer", fontSize: "14px", flexShrink: 0 }}
                                                    onClick={() => remove(field.name)}
                                                />
                                            </Flex>
                                            {/* Row 2: Type, Width & Read Only */}
                                            <Flex vertical >
                                                <Form.Item name={[index, "inputType"]} style={{ marginBottom: 0 }} initialValue="input" label={<span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>{t('Type')}</span>}>
                                                    <Select
                                                        placeholder={t('Type')}
                                                        options={inputTypeOptions}
                                                        onChange={(value) => handleInputTypeChange(index, value)}
                                                    />
                                                </Form.Item>
                                                <Flex gap={12} align="flex-end" wrap="wrap">
                                                    <Form.Item name={[index, "width"]} style={{ marginBottom: 0, flex: "1 1 148px", minWidth: "148px" }} label={<span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>{t('Width')}</span>}>
                                                        <InputNumber
                                                            min={40}
                                                            placeholder={t('Auto')}
                                                            style={{ width: "100%" }}
                                                            controls={false}
                                                            addonAfter="px"
                                                        />
                                                    </Form.Item>
                                                    <div style={{ flex: "0 0 120px", minWidth: "120px", display: "flex", flexDirection: "column", gap: "4px" }}>
                                                        <span style={{ fontSize: "12px", color: "#666", fontWeight: 500, marginBottom: "2px" }}>{t('Options')}</span>
                                                        <Form.Item name={[index, "readOnly"]} valuePropName="checked" style={{ marginBottom: 0 }} className="compact-checkbox-item" initialValue={false}>
                                                            <Checkbox style={{ fontSize: "12px", lineHeight: 1.2 }}>
                                                                {t('Read Only')}
                                                            </Checkbox>
                                                        </Form.Item>
                                                        <Form.Item name={[index, "mandatory"]} valuePropName="checked" style={{ marginBottom: 0 }} className="compact-checkbox-item" initialValue={false}>
                                                            <Checkbox style={{ fontSize: "12px", lineHeight: 1.2 }}>
                                                                {t('Mandatory')}
                                                            </Checkbox>
                                                        </Form.Item>
                                                        {watchedInputType === "user" && (
                                                            <Form.Item name={[index, "isRequestor"]} valuePropName="checked" style={{ marginBottom: 0 }} className="compact-checkbox-item" initialValue={false}>
                                                                <Checkbox style={{ fontSize: "12px", lineHeight: 1.2 }}>
                                                                    {t('Is Requestor')}
                                                                </Checkbox>
                                                            </Form.Item>
                                                        )}
                                                    </div>
                                                </Flex>
                                                {/* {watchedInputType === "dropdown" && (
                                                    <Form.Item name={[index, "multiSelect"]} valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                                                        <Checkbox style={{ fontSize: "12px" }}>
                                                            Multi Select
                                                        </Checkbox>
                                                    </Form.Item>
                                                )} */}
                                            </Flex>


                                            {/* Computed Number for number columns */}
                                            {watchedInputType === "number" && (
                                                <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff", borderRadius: "4px", border: "1px dashed #d9d9d9" }}>
                                                    <Flex gap={12} align="center" style={{ marginBottom: "8px" }}>
                                                        <Form.Item name={[index, "computedNumber"]} valuePropName="checked" style={{ marginBottom: "0 !important" }} initialValue={false}>
                                                            <Checkbox style={{ fontSize: "12px" }} onChange={(e) => handleComputedToggle(index, e.target.checked)}>
                                                                {t('Computed Number')}
                                                            </Checkbox>
                                                        </Form.Item>
                                                    </Flex>
                                                    {form.getFieldValue(['columns', index, 'computedNumber']) && (
                                                        <>
                                                            {form.getFieldValue(['columns', index, 'formulaHtml']) && (
                                                                <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>
                                                                    {t('Formula:')} <span dangerouslySetInnerHTML={{ __html: form.getFieldValue(['columns', index, 'formulaHtml']) }} />
                                                                </div>
                                                            )}
                                                            <Form.Item name={[index, "formula"]} style={{ marginBottom: 0 }}>
                                                                <Mentions
                                                                    placeholder={t('e.g. @Column1 + @Column2')}
                                                                    onChange={(val) => handleComputedFormulaChange(index, val)}
                                                                    autoSize
                                                                    className="formula-field"
                                                                    options={getOtherNumberColumns(index)}
                                                                />
                                                            </Form.Item>
                                                            <Form.Item name={[index, "formulaHtml"]} hidden>
                                                                <Input />
                                                            </Form.Item>
                                                        </>
                                                    )}
                                                </div>
                                            )}

                                            {watchedInputType === "radio" && (
                                                <>
                                                    {/* <Flex gap={12} style={{ marginTop: "8px" }}>
                                                        <Form.Item name={[index, "isArchive"]} valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                                                            <Checkbox style={{ fontSize: "12px" }} onChange={(e) => handleIsArchiveChange(index, e.target.checked)}>
                                                                Is Archive
                                                            </Checkbox>
                                                        </Form.Item>
                                                        <Form.Item name={[index, "hideInitially"]} valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                                                            <Checkbox style={{ fontSize: "12px" }}>
                                                                Hide Initially
                                                            </Checkbox>
                                                        </Form.Item>
                                                    </Flex> */}
                                                    {form.getFieldValue(['columns', index, 'isArchive']) && (
                                                        <Form.Item
                                                            name={[index, "archiveValue"]}
                                                            label={<span style={{ fontSize: "12px", color: "#666" }}>{t('Archive Value')}</span>}
                                                            style={{ marginTop: "8px", marginBottom: "8px" }}
                                                        >
                                                            <Select
                                                                placeholder={t('Select archive value')}
                                                                options={(form.getFieldValue(['columns', index, 'options']) || [])
                                                                    .filter(opt => opt && opt.label)
                                                                    .map(opt => ({ label: opt.label, value: opt.value }))}
                                                            />
                                                        </Form.Item>
                                                    )}
                                                </>
                                            )}

                                            {/* Checkbox/Radio/MCQ Label Configuration */}
                                            {(watchedInputType === "checkbox" || watchedInputType === "radio" || watchedInputType === "mcq") && (
                                                <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff", borderRadius: "4px", border: "1px dashed #d9d9d9" }}>
                                                    <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>{watchedInputType === "checkbox" ? "Checkbox" : watchedInputType === "radio" ? "Radio" : "Multiple choice"} {t('Labels:')}</div>
                                                    <Form.List name={[index, "options"]}>
                                                        {(labelFields, { add: addLabel, remove: removeLabel }) => (
                                                            <>
                                                                {labelFields.map((labelField, labelIndex) => (
                                                                    <Flex gap={8} key={labelField.key} style={{ marginBottom: "8px" }}>
                                                                        <Form.Item
                                                                            name={[labelIndex, "label"]}
                                                                            style={{ marginBottom: 0, flex: 1 }}
                                                                            rules={[{ required: true, whitespace: true, message: `${watchedInputType} label is required` }]}
                                                                        >
                                                                            <Input
                                                                                placeholder={`Enter ${watchedInputType} label`}
                                                                            />
                                                                        </Form.Item>
                                                                        {/* <Form.Item 
                                                                            name={[labelIndex, "value"]} 
                                                                            style={{ marginBottom: 0, flex: 1 }}
                                                                        >
                                                                            <Input 
                                                                                placeholder="Enter value (optional)" 
                                                                                size="small"
                                                                            />
                                                                        </Form.Item> */}
                                                                        <DeleteOutlined
                                                                            style={{ color: "red", cursor: "pointer" }}
                                                                            onClick={() => removeLabel(labelField.name)}
                                                                        />
                                                                    </Flex>
                                                                ))}

                                                                <Button
                                                                    type="dashed"
                                                                    onClick={() => addLabel({ label: "", value: uuidv4() })}
                                                                    size="small"
                                                                    icon={<PlusOutlined />}
                                                                    style={{ display: watchedInputType === "checkbox" && labelFields.length === 1 ? "none" : "block" }}
                                                                >
                                                                    {t('Add')} {watchedInputType === "checkbox" ? "Checkbox" : watchedInputType === "radio" ? "Radio" : "Multiple choice"} {t('Label')}
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Form.List>
                                                </div>
                                            )}

                                            {/* Time Configuration */}
                                            {watchedInputType === "time" && (
                                                <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff", borderRadius: "4px", border: "1px dashed #d9d9d9" }}>
                                                    <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>{t('Time Configuration:')}</div>

                                                    <Form.Item name={[index, "timeFormat"]} label={t('Time Format')} style={{ marginBottom: "12px" }}>
                                                        <Select
                                                            placeholder={t('Select time format')}
                                                            defaultValue="HH:mm"
                                                            options={[
                                                                { label: '24 Hour (HH:mm)', value: 'HH:mm' },
                                                                { label: '24 Hour with Seconds (HH:mm:ss)', value: 'HH:mm:ss' },
                                                                { label: '12 Hour (hh:mm A)', value: 'hh:mm A' },
                                                                { label: '12 Hour with Seconds (hh:mm:ss A)', value: 'hh:mm:ss A' }
                                                            ]}
                                                        />
                                                    </Form.Item>

                                                    <Form.Item name={[index, "minuteStep"]} label={t('Minute Step')} style={{ marginBottom: "12px" }}>
                                                        <Select
                                                            placeholder={t('Select minute step')}
                                                            defaultValue={1}
                                                            options={[
                                                                { label: '1 minute', value: 1 },
                                                                { label: '5 minutes', value: 5 },
                                                                { label: '10 minutes', value: 10 },
                                                                { label: '15 minutes', value: 15 },
                                                                { label: '30 minutes', value: 30 }
                                                            ]}
                                                        />
                                                    </Form.Item>

                                                    <Form.Item name={[index, "hourStep"]} label={t('Hour Step')} style={{ marginBottom: "12px" }}>
                                                        <Select
                                                            placeholder={t('Select hour step')}
                                                            defaultValue={1}
                                                            options={[
                                                                { label: '1 hour', value: 1 },
                                                                { label: '2 hours', value: 2 },
                                                                { label: '3 hours', value: 3 },
                                                                { label: '6 hours', value: 6 },
                                                                { label: '12 hours', value: 12 }
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </div>
                                            )}

                                            {/* Dropdown Options Section */}
                                            {watchedInputType === "dropdown" && (
                                                <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff", borderRadius: "4px", border: "1px dashed #d9d9d9" }}>
                                                    <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>{t('Connect Master Data:')}</div>
                                                    <Flex vertical gap={8} style={{ marginBottom: "12px" }}>
                                                        <Form.Item name={[index, "parentTable"]} style={{ marginBottom: 0, flex: 1 }}>
                                                            <Select
                                                                allowClear
                                                                placeholder={t('Select master table')}
                                                                options={masterTableOptions}
                                                                onChange={(value) => handleMasterTableChange(index, value)}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item name={[index, "mappedColumn"]} style={{ marginBottom: 0, flex: 1 }}>
                                                            <Select
                                                                allowClear
                                                                placeholder={t('Select master column')}
                                                                options={masterColumnOptions}
                                                                disabled={!selectedMasterTable}
                                                                onChange={(value) => handleMasterColumnChange(index, value)}
                                                            />
                                                        </Form.Item>
                                                    </Flex>

                                                    {isConnectedToMaster && (
                                                        <div style={{ marginBottom: "8px", fontSize: "12px", color: "#389e0d" }}>
                                                            {t('Options are synced from master data.')}
                                                        </div>
                                                    )}

                                                    {!isConnectedToMaster && (
                                                        <>
                                                            <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>{t('Dropdown Options:')}</div>
                                                            <Form.List name={[index, "options"]}>
                                                                {(optionFields, { add: addOption, remove: removeOption }) => (
                                                                    <>
                                                                        {optionFields.map((optionField, optionIndex) => (
                                                                            <Flex gap={8} key={optionField.key} style={{ marginBottom: "8px" }}>
                                                                                <Form.Item
                                                                                    name={[optionIndex, "label"]}
                                                                                    style={{ marginBottom: 0, flex: 1 }}
                                                                                    rules={[{ required: true, whitespace: true, message: "Option is required" }]}
                                                                                >
                                                                                    <Input
                                                                                        placeholder={t('Enter option')}
                                                                                    />
                                                                                </Form.Item>
                                                                                <DeleteOutlined
                                                                                    style={{ color: "red", cursor: "pointer" }}
                                                                                    onClick={() => removeOption(optionField.name)}
                                                                                />
                                                                            </Flex>
                                                                        ))}
                                                                        <Button
                                                                            type="dashed"
                                                                            onClick={() => addOption({ label: "", value: uuidv4() })}
                                                                            size="small"
                                                                            icon={<PlusOutlined />}
                                                                        >
                                                                            {t('Add Option')}
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </Form.List>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Flex>

                        <Form.Item>
                            <Tooltip title={t("Add Column")}>
                                <Button
                                    className="success-button"
                                    onClick={() => add({ id: uuidv4(), label: "", inputType: "input", options: [], readOnly: false, width: undefined })}
                                    icon={<PlusOutlined />}
                                >{t('Add Column')}</Button>
                            </Tooltip>
                        </Form.Item>
                    </>
                )}
            </Form.List>
        </>
    )
}

export default TableForm
